import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Highlighter, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const sb = supabase as any;

interface Highlight {
  id: string;
  start_offset: number;
  end_offset: number;
  color: string;
  selected_text: string;
  note?: string | null;
}

interface HighlightableTextProps {
  mcqId: string;
  text: string;
  className?: string;
  /** Tag to render the text in (default: span) */
  as?: "span" | "p" | "div";
}

const COLORS = [
  { name: "yellow", bg: "bg-yellow-200 dark:bg-yellow-900/40" },
  { name: "green", bg: "bg-green-200 dark:bg-green-900/40" },
  { name: "blue", bg: "bg-blue-200 dark:bg-blue-900/40" },
  { name: "pink", bg: "bg-pink-200 dark:bg-pink-900/40" },
];

const colorClass = (c: string) =>
  COLORS.find((x) => x.name === c)?.bg ?? COLORS[0].bg;

/**
 * Renders text with persistent user-created highlights.
 * Select any portion of text to surface a small toolbar that saves
 * the highlight to `mcq_highlights` (RLS: per-user).
 */
export const HighlightableText = ({
  mcqId,
  text,
  className,
  as: Tag = "div",
}: HighlightableTextProps) => {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [toolbar, setToolbar] = useState<{
    x: number;
    y: number;
    start: number;
    end: number;
    selected: string;
  } | null>(null);

  // Load existing highlights
  useEffect(() => {
    if (!user || !mcqId) return;
    (async () => {
      const { data } = await sb
        .from("mcq_highlights")
        .select("id, start_offset, end_offset, color, selected_text, note")
        .eq("mcq_id", mcqId)
        .eq("user_id", user.id);
      if (data) setHighlights(data);
    })();
  }, [user, mcqId]);

  // Detect text selection within this container
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !containerRef.current) {
      setToolbar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!containerRef.current.contains(range.commonAncestorContainer)) {
      setToolbar(null);
      return;
    }
    const selected = sel.toString();
    if (!selected.trim()) {
      setToolbar(null);
      return;
    }
    // Compute offsets relative to plain text
    const start = text.indexOf(selected);
    if (start < 0) {
      setToolbar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    setToolbar({
      x: rect.left + rect.width / 2 - containerRect.left,
      y: rect.top - containerRect.top - 8,
      start,
      end: start + selected.length,
      selected,
    });
  }, [text]);

  const saveHighlight = async (color: string) => {
    if (!toolbar || !user) return;
    const { data, error } = await sb
      .from("mcq_highlights")
      .insert({
        mcq_id: mcqId,
        user_id: user.id,
        start_offset: toolbar.start,
        end_offset: toolbar.end,
        selected_text: toolbar.selected,
        color,
      })
      .select("id, start_offset, end_offset, color, selected_text, note")
      .maybeSingle();
    if (error) {
      toast.error("Couldn't save highlight");
      return;
    }
    if (data) {
      setHighlights((prev) => [...prev, data]);
      toast.success("Highlight saved");
    }
    setToolbar(null);
    window.getSelection()?.removeAllRanges();
  };

  const deleteHighlight = async (id: string) => {
    const { error } = await sb.from("mcq_highlights").delete().eq("id", id);
    if (error) {
      toast.error("Couldn't remove highlight");
      return;
    }
    setHighlights((prev) => prev.filter((h) => h.id !== id));
    toast.success("Highlight removed");
  };

  // Render text with highlights merged in (handles overlaps by sorting)
  const segments = (() => {
    if (highlights.length === 0) return [{ text, hl: null as Highlight | null }];
    const sorted = [...highlights].sort((a, b) => a.start_offset - b.start_offset);
    const result: { text: string; hl: Highlight | null }[] = [];
    let cursor = 0;
    for (const h of sorted) {
      if (h.start_offset < cursor) continue; // skip overlapping for now
      if (h.start_offset > cursor) {
        result.push({ text: text.slice(cursor, h.start_offset), hl: null });
      }
      result.push({
        text: text.slice(h.start_offset, h.end_offset),
        hl: h,
      });
      cursor = h.end_offset;
    }
    if (cursor < text.length) result.push({ text: text.slice(cursor), hl: null });
    return result;
  })();

  return (
    <div className="relative">
      <Tag
        ref={containerRef as any}
        onMouseUp={handleMouseUp}
        className={cn("select-text", className)}
      >
        {segments.map((seg, i) =>
          seg.hl ? (
            <Popover key={`${seg.hl.id}-${i}`}>
              <PopoverTrigger asChild>
                <mark
                  className={cn(
                    "rounded px-0.5 cursor-pointer transition-colors",
                    colorClass(seg.hl.color)
                  )}
                >
                  {seg.text}
                </mark>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2" align="center">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => deleteHighlight(seg.hl!.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </PopoverContent>
            </Popover>
          ) : (
            <span key={i}>{seg.text}</span>
          )
        )}
      </Tag>

      {toolbar && (
        <div
          className="absolute z-50 -translate-x-1/2 -translate-y-full bg-popover border rounded-lg shadow-lg p-1 flex items-center gap-1 animate-fade-in"
          style={{ left: toolbar.x, top: toolbar.y }}
        >
          <Highlighter className="h-3.5 w-3.5 text-muted-foreground ml-1" />
          {COLORS.map((c) => (
            <button
              key={c.name}
              onClick={() => saveHighlight(c.name)}
              className={cn(
                "w-5 h-5 rounded-full border border-border hover:scale-110 transition-transform",
                c.bg
              )}
              aria-label={`Highlight ${c.name}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
